// „Оборот по работни дни" — целият избран период (без бъдещи дни), но
// най-малко две седмици, за да се вижда кратък период сред съседните си дни.
// Кои дни идват решава BE-то (`daily_reports`); картата рисува каквото получи.
//
// Стои НАЙ-ДОЛУ съзнателно: показва къде стои избраният период спрямо
// съседните дни, а това е контекст, не първото нещо, което човек гледа.
// Дните вътре в периода са акцентни, днешният е щрихован (още не е завършил),
// пунктираната линия е средното по дните с оборот.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { fmtEuro } from "../../../../utils/currency";
import { parseDecimal } from "../../../../components/dataTable";
import { businessDateOf } from "../../../../lib/businessDay";
import type { DashboardDailyReport } from "../../../../api/reportsDashboard";

/**
 * Съкращението на деня („чт", „Thu") — през `Intl`, а НЕ през dayjs: в проекта
 * dayjs върви без заредени локали и `format("dd")` би дало английско име и на
 * български интерфейс.
 */
const weekdayLabel = (date: string, language: string): string =>
  new Intl.DateTimeFormat(language, { weekday: "short" }).format(
    new Date(`${date}T00:00:00`),
  );

/** Височина на зоната със стълбчетата. */
const PLOT_HEIGHT = 132;
/**
 * Височината, върху която реално се мащабират стълбчетата: над всяко стои
 * редът с числото. Средната линия ползва СЪЩАТА стойност — с `PLOT_HEIGHT`
 * тя стоеше ~20 % по-високо и ден, равен точно на средното, изглеждаше под
 * средното.
 */
const BAR_SCALE_HEIGHT = PLOT_HEIGHT - 22;
/** Най-ниското видимо стълбче — ден с оборот не бива да изчезва. */
const MIN_BAR_HEIGHT = 2;
/** Най-тясната колонка (стълбче + етикет „пт 21") — под това етикетите се
 *  застъпват; при повече дни графиката скролва в своя контейнер. */
const MIN_BAR_COLUMN_WIDTH = 38;

type Props = {
  days: DashboardDailyReport[];
  /** Границите на избрания период (локални дати YYYY-MM-DD). */
  periodStart: string | null | undefined;
  periodEnd: string | null | undefined;
  /** Липсва, когато „История на маси" е зад право, което този няма. */
  onPickDay?: (date: string) => void;
  isNarrow: boolean;
};

/** „1.2к" над хиляда, иначе цялото число; празен низ при нула. */
const shortAmount = (value: number, thousandSuffix: string): string => {
  if (!value) return "";
  return value >= 1000
    ? `${(value / 1000).toFixed(1)}${thousandSuffix}`
    : String(Math.round(value));
};

export const RevenueDaysCard = ({
  days,
  periodStart,
  periodEnd,
  onPickDay,
  isNarrow,
}: Props) => {
  const { t, i18n } = useTranslation();

  const bars = useMemo(() => {
    // BE-то връща най-новия ден пръв; графиката се чете отляво надясно.
    const ordered = [...days].reverse();
    // Днешният ден по РАБОТНА дата — в 00:30 „днес" е още вчерашният.
    const today = businessDateOf(dayjs()).format("YYYY-MM-DD");
    return ordered.map((day) => {
      const value =
        day.total_amount === null ? 0 : parseDecimal(day.total_amount);
      const inPeriod =
        !!periodStart &&
        !!periodEnd &&
        day.date >= periodStart &&
        day.date <= periodEnd;
      return { date: day.date, value, inPeriod, isToday: day.date === today };
    });
  }, [days, periodStart, periodEnd]);

  const max = Math.max(1, ...bars.map((bar) => bar.value));
  const withRevenue = bars.filter((bar) => bar.value > 0);
  const average = withRevenue.length
    ? withRevenue.reduce((sum, bar) => sum + bar.value, 0) / withRevenue.length
    : 0;

  const rangeLabel = bars.length
    ? `${dayjs(bars[0].date).format("DD.MM")} – ${dayjs(
        bars[bars.length - 1].date,
      ).format("DD.MM")}`
    : "";

  return (
    <section className="rounded-xl border border-shade-2 bg-surface px-4 py-3.5">
      <header
        className={`mb-3 flex gap-2.5 ${
          isNarrow ? "flex-col" : "items-baseline"
        }`}
      >
        <h2 className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
          {t("reportsPage.dashDaysTitle")}
        </h2>
        <p className="text-[12px] font-semibold text-soft">
          {t("reportsPage.dashDaysHint", { range: rangeLabel })}
        </p>
        <span className="flex-1" />
        {average > 0 ? (
          <p className="font-pos-mono text-[12px] font-semibold tabular-nums text-muted">
            {t("reportsPage.dashDaysAverage", { amount: fmtEuro(average) })}
          </p>
        ) : null}
      </header>

      {withRevenue.length ? (
        // Четиринадесет дни с етикет „пт 21" не се събират на телефон, а
        // цял месец — и на компютър. Графиката скролва в СВОЯ контейнер —
        // страницата не бива да скролва настрани заради нея. Ширината идва
        // от броя дни (стойност по време на изпълнение → inline style).
        <div className="-mx-1 overflow-x-auto px-1">
          <div style={{ minWidth: bars.length * MIN_BAR_COLUMN_WIDTH }}>
            <div
              className="relative flex h-[132px] items-end gap-1.5">
              {/* Средната линия — позицията ѝ идва от данните. */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 border-t border-dashed border-ghost"
                style={{ bottom: (average / max) * BAR_SCALE_HEIGHT }}
              />
              {bars.map((bar) => (
                <button
                  key={bar.date}
                  type="button"
                  onClick={() => onPickDay?.(bar.date)}
                  disabled={!onPickDay}
                  title={`${dayjs(bar.date).format("DD.MM.YYYY")} · ${fmtEuro(bar.value)}`}
                  className="group flex h-full flex-1 flex-col items-center justify-end gap-1"
                >
                  <span
                    className={`font-pos-mono text-[10px] font-semibold tabular-nums ${
                      bar.inPeriod ? "text-brand-deep-ink" : "text-faint"
                    }`}
                  >
                    {shortAmount(bar.value, t("reportsPage.dashThousandShort"))}
                  </span>
                  <span
                    className={`relative w-full max-w-[44px] overflow-hidden rounded-t-[4px] transition-opacity group-hover:opacity-80 ${
                      bar.inPeriod ? "bg-brand" : "bg-brand-tint2"
                    }`}
                    style={{
                      height: Math.max(
                        MIN_BAR_HEIGHT,
                        (bar.value / max) * BAR_SCALE_HEIGHT,
                      ),
                    }}
                  >
                    {bar.isToday ? (
                      // Днешният ден още не е завършил — щриховката го казва.
                      <span className="absolute inset-0 bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.38)_0_4px,transparent_4px_8px)]" />
                    ) : null}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-1.5 flex gap-1.5">
              {bars.map((bar) => (
                <span
                  key={bar.date}
                  className={`flex-1 whitespace-nowrap text-center font-pos-mono text-[10px] leading-none ${
                    bar.inPeriod
                      ? "font-bold text-ink"
                      : "font-semibold text-muted"
                  }`}
                >
                  {`${weekdayLabel(bar.date, i18n.language)} ${dayjs(bar.date).format("DD")}`}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p className="py-8 text-center text-[12.5px] font-medium text-muted">
          {t("reportsPage.dashDaysEmpty", { range: rangeLabel })}
        </p>
      )}
    </section>
  );
};
